/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import { useLanguage } from "LanguageContext";
import React, { useState } from "react";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import AnalysisIcons from "../../../CommonUi/AnalysisIcons/AnalysisIcons";
import {COMPLIANCE_COLOR_CODES} from "../../../../services/config";

type ComplianceCode = "-1" | "0" | "1" | "2" | "3";

type AnalysisTabsProps = {
    analysis?: any;
  };

const Tabs: React.FC<AnalysisTabsProps> = ({
  analysis,
}) => {
  const DEFAULT_COLOR = "#EAEEF0";
  const VALID_CATEGORIES = ["-1", "0", "1", "2", "3"];

  const allDomains = (analysis ?? []).flatMap((item: any) => item?.domains ?? []);

  const domainNames: string[] = [];
  const domainColors: string[] = [];

  allDomains.forEach((domain: any) => {
    if (!domain?.domain_name) {
      return;
    }

    const category = domain?.category?.toString();
    let color = DEFAULT_COLOR;

    if (VALID_CATEGORIES.includes(category)) {
      color = COMPLIANCE_COLOR_CODES[category as ComplianceCode];
    }

    domainNames.push(domain.domain_name);
    domainColors.push(color);
  });

  const [activeTab, setActiveTab] = useState(domainNames?.[0]);
  const theme = useTheme();
  const { language } = useLanguage();
  const { analysisContextResult } = useAnalysis();

const tabContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 0px;
  margin-bottom: 40px;
`;

const tabButtonStyle = css`
  display: flex;
  padding: 4px 6px 12px;
  border: none;
  background: transparent;
  border-bottom: 2px solid transparent;
  font-family: ${theme.typography.bodyMedium.fontFamily};
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: ${theme.colors.textMedium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  > span{
    margin-inline-end: .5rem;
  }
`;

const activeTabStyle = css`
  color: ${theme.colors.primary};
  border-bottom: 2px solid ${theme.colors.primary};
  font-weight: 600;
`;

const scrollToResultItem = (item: string) => {
    if (typeof document !== "undefined") {
      const element = document.getElementById(item);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        element.click();
      }
    }
  };

  return (
    <div css={tabContainerStyle}>
      {domainNames?.map((tab: string, index: number) => (
        <button
          key={index}
          onClick={() => {setActiveTab(tab); scrollToResultItem(tab)}}
          css={[tabButtonStyle, activeTab === tab && activeTabStyle]}
        >
          <AnalysisIcons show="smallIcon" imgName={tab} colorId={domainColors[domainNames.indexOf(tab)]}/>
          {window?.referencesFramework?.[tab]?.[analysisContextResult?.locale_code].title ?? tab}
        </button>
      ))}
    </div>
  );
};

export default Tabs;
